import {
  Collapse,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { capitalize, isEmpty, isNil, map } from 'lodash';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import BatchShippingIcon, { batchShippingTooltip } from 'common/components/BatchShippingIcon';
import BusinessAddressIndicator from 'common/components/BusinessAddressIndicator';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import BatchShipmentDialog from 'common/modules/BatchShipmentDialog';
import { getTrackingInfo } from 'common/utils';
import ShipmentOrderLineItemTable from './ShipmentOrderLineItemTable';
import ShippingOptions from './ShippingOptions';

const ShipmentOrderRow = ({ shipment, mpi }) => {
  const [showDetails, setShowDetails] = useState();
  const [openDialog, setOpenDialog] = useState(false);
  const [currentBatchShipmentId, setBatchShipmentId] = useState(null);

  const toggleDetails = () => setShowDetails((show) => !show);

  const handleOpenDialog = (batchShipmentId) => {
    setBatchShipmentId(batchShipmentId);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setBatchShipmentId(null);
    setOpenDialog(false);
  };

  return (
    <>
      <BatchShipmentDialog
        batchShipmentId={currentBatchShipmentId}
        onClose={handleClose}
        open={openDialog}
      />

      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset', bgcolor: showDetails ? '#0000000a' : 'inherit' },
        }}
        hover
      >
        <TableCell sx={{ py: 1 }}>
          <IconButton
            aria-label={`Toggle Details ${shipment.orderShipmentId}`}
            size='small'
            onClick={toggleDetails}
          >
            {showDetails ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align='center' sx={{ py: 1 }}>
          {shipment.shipmentCreatedDate
            ? format(new Date(shipment.shipmentCreatedDate), 'MM/dd/yyyy hh:mm a')
            : 'N/A'}
        </TableCell>
        <TableCell align='center' sx={{ py: 1 }}>
          <Typography>{shipment.orderShipmentId}</Typography>
        </TableCell>
        <TableCell align='center' sx={{ py: 1 }}>
          {format(new Date(shipment.shipmentUpdatedDate), 'MM/dd/yyyy hh:mm a')}
        </TableCell>
        <TableCell align='center' sx={{ py: 1 }}>
          {shipment.shipmentData?.trackingNumber ? (
            <Link
              href={
                getTrackingInfo({ trackingNumber: shipment.shipmentData.trackingNumber })
                  .trackingUrl
              }
              target='_blank'
              sx={{ fontSize: '1em' }}
            >
              {shipment.shipmentData.trackingNumber}
            </Link>
          ) : (
            <Typography variant='body2'>N/A</Typography>
          )}
        </TableCell>
        {shipment.batchShipmentId && (
          <TableCell
            sx={{ cursor: 'pointer', textDecoration: 'underline', py: 1 }}
            onClick={() => handleOpenDialog(shipment.batchShipmentId)}
            align='center'
          >
            {shipment.batchShipmentId}
          </TableCell>
        )}
        {!shipment.batchShipmentId && (
          <TableCell align='center' sx={{ py: 1 }}>
            NA
          </TableCell>
        )}

        <TableCell align='center'>{capitalize(shipment.shipmentSpType)}</TableCell>

        <TableCell align='center'>
          {shipment.batchShipmentId && (
            <Tooltip
              title={batchShippingTooltip({
                isBusinessAddress: shipment.isBusinessAddress,
                isShipped: true,
                isClickable: true,
              })}
              arrow
              placement='top'
            >
              <BatchShippingIcon
                color={perfectRxTheme.palette.success.main}
                onClick={() => handleOpenDialog(shipment.batchShipmentId)}
                showBadge={shipment.isBusinessAddress}
              />
            </Tooltip>
          )}
          {!shipment.batchShipmentId && shipment.isBusinessAddress && <BusinessAddressIndicator />}
        </TableCell>

        <TableCell align='center'>
          <ShippingOptions orderShipmentId={shipment.orderShipmentId} mpi={mpi} />
        </TableCell>
      </TableRow>

      <TableRow sx={{ '& > *': { borderTop: 'unset' } }}>
        <TableCell colSpan={8} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={showDetails} orientation='vertical' timeout='auto'>
            {shipment?.lineItems && (
              <ShipmentOrderLineItemTable
                lineItemRow={shipment.orderShipmentId}
                lineItems={shipment.lineItems}
              />
            )}
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

ShipmentOrderRow.propTypes = {
  mpi: PropTypes.string.isRequired,
  shipment: PropTypes.shape({
    orderShipmentId: PropTypes.string,
    shipmentCreatedDate: PropTypes.string,
    shipmentUpdatedDate: PropTypes.string,
    shipmentData: PropTypes.shape({
      modifyDate: PropTypes.string,
      shipDate: PropTypes.string,
      trackingNumber: PropTypes.string,
    }),
    batchShipmentId: PropTypes.string,
    lineItems: PropTypes.arrayOf(PropTypes.shape({})),
    shipmentSpType: PropTypes.string,
    isBusinessAddress: PropTypes.bool,
  }).isRequired,
};

const ShipmentTable = ({ mpi }) => {
  const shipments = useSelector(({ patient }) => patient[mpi]?.shipmentHistory?.shipments);

  return (
    <>
      <TableContainer>
        <Table aria-label='ShipmentList' padding='checkbox'>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell align='center'>
                <Typography sx={{ fontWeight: '600' }}>Date Created</Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography sx={{ fontWeight: '600' }}>Shipment Id</Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography sx={{ fontWeight: '600' }}>Last Updated</Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography sx={{ fontWeight: '600' }}>Tracking Number</Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography sx={{ fontWeight: '600' }}>Batch Shipment Id</Typography>
              </TableCell>
              <TableCell align='center'>
                <Typography sx={{ fontWeight: '600' }}>Vendor</Typography>
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {map(shipments, (shipment) => (
              <ShipmentOrderRow key={shipment.orderShipmentId} shipment={shipment} mpi={mpi} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {isNil(shipments) && <LoadingModule height='25vh' />}

      {!isNil(shipments) && isEmpty(shipments) && <NothingFound height='25vh' />}
    </>
  );
};

ShipmentTable.propTypes = {
  mpi: PropTypes.string.isRequired,
};

export default ShipmentTable;
